/* eslint-disable @typescript-eslint/no-magic-numbers */
import { ScreenSize } from '@libs/shared-services';
import { isNotNull } from '@libs/utils';
import {
  CEJ_PERCENTAGE,
  Coordinates,
  GUM_PADDING_X,
  Jaw,
  MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE,
  MeasurementCoordinates,
  PERIO_TEETH_CONTAINER_PADDING_X,
  Tooth,
  TOOTH_PERIO_HEIGHT,
  TOOTH_WIDTH,
  ToothSide,
} from '@libs/ui-lib';

export const calculateCoordinates = (
  teeth: Tooth[],
  screenSize: ScreenSize,
  containerWidth: number,
  teethTranslateY?: { upper: { buccal: number; lingual: number }; lower: { buccal: number; lingual: number } },
  gumIntermediateBump?: (jaw: Jaw, side: ToothSide) => number,
): {
  upperJawTeeth: Tooth[];
  lowerJawTeeth: Tooth[];
  upperJawBuccalTeethCoordinates: Record<number, Coordinates>;
  upperJawLingualTeethCoordinates: Record<number, Coordinates>;
  lowerJawLingualTeethCoordinates: Record<number, Coordinates>;
  lowerJawBuccalTeethCoordinates: Record<number, Coordinates>;
  upperJawBuccalPeriosCoordinates: MeasurementCoordinates[];
  upperJawLingualPeriosCoordinates: MeasurementCoordinates[];
  lowerJawLingualPeriosCoordinates: MeasurementCoordinates[];
  lowerJawBuccalPeriosCoordinates: MeasurementCoordinates[];
} => {
  // 1. separate teeth by jaw
  const upperJawTeeth = teeth.filter((tooth) => tooth.jaw === 'upper');
  const lowerJawTeeth = teeth.filter((tooth) => tooth.jaw === 'lower').sort(({ position: posA }, { position: posB }) => posB - posA);
  // 2. get all teeth coordinates
  const upperJawTeethCoordinates = getTeethCoordinates(upperJawTeeth, screenSize, containerWidth);
  const upperJawBuccalTeethCoordinates = translateY(upperJawTeethCoordinates, teethTranslateY?.upper.buccal ?? 0);

  const upperJawLingualTeethCoordinates = translateY(upperJawTeethCoordinates, teethTranslateY?.upper.lingual ?? 0);

  const lowerJawTeethCoordinates = getTeethCoordinates(lowerJawTeeth, screenSize, containerWidth);
  const lowerJawLingualTeethCoordinates = translateY(lowerJawTeethCoordinates, teethTranslateY?.lower.lingual ?? 0);
  const lowerJawBuccalTeethCoordinates = translateY(lowerJawTeethCoordinates, teethTranslateY?.lower.buccal ?? 0);

  // 3. based on teeth coordinates, get perios coordinates and add intermediate bumps for gum drawing
  const upperJawBuccalPeriosCoordinates = getMeasurementsCoordinates(
    upperJawTeeth,
    upperJawBuccalTeethCoordinates,
    'buccal',
    'lower',
    gumIntermediateBump,
  );

  const upperJawLingualPeriosCoordinates = getMeasurementsCoordinates(
    upperJawTeeth,
    upperJawLingualTeethCoordinates,
    'lingual',
    'lower',
    gumIntermediateBump,
  );

  const lowerJawLingualPeriosCoordinates = getMeasurementsCoordinates(
    lowerJawTeeth,
    lowerJawLingualTeethCoordinates,
    'lingual',
    'upper',
    gumIntermediateBump,
  );
  const lowerJawBuccalPeriosCoordinates = getMeasurementsCoordinates(
    lowerJawTeeth,
    lowerJawBuccalTeethCoordinates,
    'buccal',
    'upper',
    gumIntermediateBump,
  );
  return {
    upperJawTeeth,
    lowerJawTeeth,

    upperJawBuccalTeethCoordinates,
    upperJawLingualTeethCoordinates,
    lowerJawLingualTeethCoordinates,
    lowerJawBuccalTeethCoordinates,

    upperJawBuccalPeriosCoordinates,
    upperJawLingualPeriosCoordinates,
    lowerJawLingualPeriosCoordinates,
    lowerJawBuccalPeriosCoordinates,
  };
};

/**
 * Calculates the coordinates of all teeth based on their position and jaw. Can handle only one jaw at a time! Make sure teeth are in order of position (1-16 or 32-17)!
 * @param teeth
 * @param screenSize
 */
export const getTeethCoordinates = (teeth: Tooth[], screenSize: ScreenSize, containerWidth: number): Record<number, Coordinates> => {
  const teethContainerWidth = containerWidth - PERIO_TEETH_CONTAINER_PADDING_X(screenSize) * 2 - GUM_PADDING_X * 2;
  const evenPadding = (teethContainerWidth - TOOTH_WIDTH(screenSize) * teeth.length) / (teeth.length - 1);
  const upperJawLeftMostPosition = 1;
  const lowerJawLeftMostPosition = 32;

  const baseCoordinates: (Coordinates & { position: number })[] = [];
  teeth.forEach(({ position }) => {
    const lastX = baseCoordinates.at(-1)?.x ?? PERIO_TEETH_CONTAINER_PADDING_X(screenSize) + GUM_PADDING_X;
    // calculating for the base of the tooth
    baseCoordinates.push({
      position,
      x:
        lastX +
        (position === upperJawLeftMostPosition || position === lowerJawLeftMostPosition ? 0 : TOOTH_WIDTH(screenSize) + evenPadding),
      y: 0,
    });
  });

  return baseCoordinates.reduce(
    (acc, { position, x, y }) => {
      acc[position] = { x, y };
      return acc;
    },
    {} as Record<number, Coordinates>,
  );
};

export const translateY = (c: Record<number, Coordinates>, v: number): Record<number, Coordinates> => {
  return Object.entries(c).reduce(
    (acc, [position, { x, y }]) => {
      acc[Number(position)] = { x, y: y + v };
      return acc;
    },
    {} as Record<number, Coordinates>,
  );
};

export const getMeasurementsCoordinates = (
  teeth: Tooth[],
  teethCoordinates: Record<number, Coordinates>,
  side: ToothSide,
  cejPosition: 'upper' | 'lower',
  gumIntermediateBump?: (jaw: Jaw, side: ToothSide) => number,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): MeasurementCoordinates[] => {
  const measurementsCoordinates: MeasurementCoordinates[] = [];

  for (const { position, perio, jaw } of teeth) {
    const baseCoordinates = teethCoordinates[position];

    // there are 3 mearsurements for each tooth: mesial, central, distal. The order of these on the UI depends on the tooth position range.
    const perioSidesInOrder =
      (position >= 9 && position <= 16) || (position >= 17 && position <= 24)
        ? (['mesial', 'central', 'distal'] as const)
        : (['distal', 'central', 'mesial'] as const);

    const coords: MeasurementCoordinates[] = [];

    for (const [index, point] of perioSidesInOrder.entries()) {
      //this is an important equation to calculate the gingival margin
      let gmValue = perio[side].clinicalAttachmentLoss[point] - perio[side].pocketDepth[point];
      const pdValue = perio[side].pocketDepth[point];
      if (!gmValue) {
        gmValue = 0;
      }

      const gingivalMarginForUpperCejPosition =
        baseCoordinates.y +
        TOOTH_PERIO_HEIGHT('xl') +
        TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * gmValue -
        TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE;

      const gingivalMarginForLowerCejPosition =
        baseCoordinates.y -
        TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * gmValue +
        TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE;

      const sum = gmValue + pdValue;
      // 12 is the max pocket depth
      const overflow = Math.max(sum - 12, 0);

      const pocketDepthYShift = TOOTH_PERIO_HEIGHT('xl') * pdValue * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE;
      //there is a correction if we have too big pocket depth, we maximize it to 12mm
      const pocketDepthOverflowCorrection = TOOTH_PERIO_HEIGHT('xl') * overflow * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE;

      //we place the 3 measurements on the tooth with this custom x shift
      let measurementXShift = 0;
      if (index === 0) {
        measurementXShift = 4;
      }
      if (index === 1) {
        measurementXShift = TOOTH_WIDTH('xl') / 3 + 8;
      }
      if (index === 2) {
        measurementXShift = (2 * TOOTH_WIDTH('xl')) / 3 + 11;
      }

      coords.push({
        pocketDepth: isNotNull(perio[side].pocketDepth[point])
          ? {
              value: perio[side].pocketDepth[point],
              x: baseCoordinates.x + measurementXShift,
              // this should be the of top of the purple triangles, which attach to the GM line.
              y:
                cejPosition === 'upper'
                  ? gingivalMarginForUpperCejPosition - pocketDepthYShift + pocketDepthOverflowCorrection
                  : gingivalMarginForLowerCejPosition - pocketDepthYShift + pocketDepthOverflowCorrection,
            }
          : undefined,
        gingivalMargin: {
          value: gmValue,
          x: gumIntermediateBump
            ? baseCoordinates.x + TOOTH_WIDTH('xl') / 3 + 10
            : baseCoordinates.x + (index * TOOTH_WIDTH('xl')) / 3 + 10,
          y: cejPosition === 'upper' ? gingivalMarginForUpperCejPosition : gingivalMarginForLowerCejPosition,
        },
        bleedingOnProbing: perio[side]?.bleedingOnProbing?.[point] ?? false,
        suppuration: perio[side]?.suppuration?.[point] ?? false,
        base: {
          x: baseCoordinates.x + measurementXShift,
          y: baseCoordinates.y,
        },
      });
    }

    const previousLastCoordinate = measurementsCoordinates.at(-1);
    const currentFirstCoordinate = coords.at(0);
    if (previousLastCoordinate && currentFirstCoordinate && gumIntermediateBump) {
      const intermediateCoordinates: MeasurementCoordinates = {
        pocketDepth:
          isNotNull(previousLastCoordinate.pocketDepth) && isNotNull(currentFirstCoordinate.pocketDepth)
            ? {
                x: previousLastCoordinate.pocketDepth.x + (currentFirstCoordinate.pocketDepth.x - previousLastCoordinate.pocketDepth.x) / 2,
                y: previousLastCoordinate.pocketDepth.y + (gumIntermediateBump?.(jaw, side) ?? 0),
              }
            : undefined,
        gingivalMargin: {
          x:
            previousLastCoordinate.gingivalMargin.x +
            (currentFirstCoordinate.gingivalMargin.x - previousLastCoordinate.gingivalMargin.x) / 2,
          y: previousLastCoordinate.gingivalMargin.y + (gumIntermediateBump?.(jaw, side) ?? 0),
        },
      };
      measurementsCoordinates.push(intermediateCoordinates, ...coords);
    } else {
      measurementsCoordinates.push(...coords);
    }
  }
  return measurementsCoordinates;
};
